import type { FlagSeverity } from 'types/flags';
import type { ConfidenceFactor, EntityType } from '../types';

// A single entity the user can select to reconcile in the tearsheet — derived
// from the party's staged entity and its suggestion match candidates (v3
// suggestions-table model).
export interface ReconcileCandidate {
  id: number;
  name: string;
  type: EntityType;
  // Tagged "Operator": a code points here or, for a primary, at one of
  // its aliases.
  operator?: boolean;
  // Primary address line for the tile (null when none on file).
  address: string | null;
  // The anchor candidate — the party's current anchor: the staged entity from
  // the document or the Deduplicated Result it was matched into. Rendered
  // frozen/first; a dedupe target only when it carries no unresolved address
  // flags.
  isStaged: boolean;
  // v3 "Source" column: "Staged Entity from Document" / "Deduplicated Result" /
  // "Suggestion N" / "User Selected" / "User Created".
  sourceLabel: string;
  // Match confidence on a 0–1 scale (null for the staged entity / user-created
  // canonicals that have no agent score).
  confidence: number | null;
  // Primary phone on file (null when none) — shown on the tile cards.
  phone: string | null;
  // True for the agent's recommended match (drives the "Recommended" badge on
  // the tile card / first AI suggestion).
  recommended: boolean;
  // True when the entity has an unresolved (non-high) address review flag — such
  // entities are listed in the flag banner and cannot be selected (§ address
  // blocking).
  flagged: boolean;
  // Consolidation-risk flag category (PROD/2907373590), or null/undefined when
  // not flagged. Unlike `flagged`, this styles the row distinctly but keeps it
  // selectable — the warning fires at submit, not at selection.
  flagCategory?: string | null;
  // Free-text reason on the flag, shown on hover over the flag chip; null/none.
  flagRationale?: string | null;
  // Chip severity: 'warning' → yellow (gated, in a workspace), 'info' → blue (an
  // interpretation only), null/undefined → unflagged or dormant (no chip).
  flagSeverity?: FlagSeverity | null;
  // Per-factor confidence breakdown for the expanded-row drilldown.
  confidenceFactors?: ConfidenceFactor[];
  // The entity's addresses (for the address picker + the row's per-address
  // display). `flagged`/`criticality` mark each address that carries an
  // unresolved review flag, so the row highlights + badges each one individually.
  addresses: Array<{
    id: number;
    label: string;
    flagged?: boolean;
    criticality?: 'high' | 'medium' | null;
  }>;
  // The row's *display* entity's own participation (same membership paths as the
  // flag, not alias-expanded). On a collapsed family the chip reflects any flagged
  // member (primary_flagged) while these count only the display member, so a
  // warning row can legitimately show "Title Workspaces: 0". titleWorkspaces feeds
  // the popup (its length is the displayed count); interpretationCount is a bare
  // count whose link opens the profile Interpretations tab filtered to the entity.
  titleWorkspaces?: Array<{ id: number; name: string }>;
  interpretationCount?: number;
  // Unresolved address-confidence flag (Phase 2 / #26). Unlike `flagged`, this
  // does NOT block selection — only submission — so it's tracked separately and
  // surfaced on the Entity Consolidation name + a submit-blocking banner.
  addressFlagged?: boolean;
  // 'high'/'medium' criticality of that flag — drives the row-level address
  // highlight + "Review: High/Med" tag (mock v25).
  addressFlagCriticality?: 'high' | 'medium' | null;
}

// A left-panel Source-cell operation tag (mock default-deduplication/v25). Tags
// mirror the survivor groups one-to-one: "Deduplicate N" (cool-gray, outline) on
// every member of group N, "Surviving Entity N" (high-contrast) on that group's
// representative. Carrying the type + outline lets the table render the mock's
// colours (Erik feedback: "Surviving Entity pill color and name incorrect").
export interface OperationTag {
  label: string;
  type: 'cool-gray' | 'teal' | 'high-contrast';
  // Transparent-fill / 1px-border treatment (Carbon ships no outline Tag variant).
  outline?: boolean;
}

// `as const` so indexing yields the literal label union ('Individual' | …),
// which is exactly the entity-list EntityType — letting the reconcile tearsheets
// reuse this map in place of their own CONCRETE_TO_LABEL duplicates. A plain
// object (not `satisfies Record<EntityType, string>`) because the toolchain's
// prettier can't parse the `satisfies` operator; it is keyed by the three
// EntityType members.
export const ENTITY_TYPE_LABEL = {
  IndividualEntity: 'Individual',
  OrganizationEntity: 'Organization',
  ImportedEntity: 'Imported',
} as const;
